import { InterpolatedProperty, SimpleSettingsProperty } from '../properties';
import { UUID, Color, TextOrientationMode, TextRenderMode } from '../types';
import { CameraRotationMode } from '../types';
import { Vector3 } from '../types';

// Add mobile detection utility
function getIsMobile(): boolean {
    const MOBILE_BREAKPOINT = 768;
    return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
}

// Add mobile-aware camera defaults
function getMobileAwareCameraDistance(): number {
    return getIsMobile() ? 2.2 : 3.8; // Closer on mobile for better visibility
}

function getMobileAwareCameraLatitude(): number {
    return getIsMobile() ? -12 : -16; // Slightly higher angle on mobile for better viewing
}

export enum LabelParentMode {
  POI = 'poi',
  POISHAPE = 'poishape'
}

export enum TextureBacksideMode {
  NEVER = 'never',
  ALWAYS = 'always',
  AUTO_PNG = 'auto_png',
  AUTO_TRANSPARENT = 'auto_transparent'
}

/**
 * 🎯 AppSettingsData - Global app settings with hierarchical properties
 * Contains label settings, input settings, and general app configuration
 * Singleton pattern for global access
 */
export class AppSettingsData {
    private static _instance: AppSettingsData | null = null;
    
    public static get Instance(): AppSettingsData {
        if (!AppSettingsData._instance) {
            AppSettingsData._instance = new AppSettingsData();
        }
        return AppSettingsData._instance;
    }
    
    public static setInstance(instance: AppSettingsData): void {
        AppSettingsData._instance = instance;
    }
    
    public readonly id: UUID;
    
    // 📷 Camera settings
    public readonly cameraRotationMode: SimpleSettingsProperty<CameraRotationMode>;
    public readonly cameraDistance: InterpolatedProperty<number>;
    public readonly cameraLatitude: InterpolatedProperty<number>;
    public readonly cameraLongitude: InterpolatedProperty<number>;
    public readonly cameraTranslationSpeed: InterpolatedProperty<number>;
    public readonly cameraRotationSpeed: InterpolatedProperty<number>;
    
    // 📱 Mobile Camera settings
    public readonly enableMobileCameraOptimization: SimpleSettingsProperty<boolean>;
    public readonly mobileCameraDistanceMultiplier: InterpolatedProperty<number>;
    public readonly mobileCameraLatitudeOffset: InterpolatedProperty<number>;
    public readonly mobileCameraSensitivityMultiplier: InterpolatedProperty<number>;
    
    // ⚡ Performance settings
    public readonly lodDistance: InterpolatedProperty<number>;
    public readonly cullingDistance: InterpolatedProperty<number>;
    public readonly enableFrustumCulling: SimpleSettingsProperty<boolean>;
    
    // 🐛 Debug settings
    public readonly showDebugInfo: SimpleSettingsProperty<boolean>;
    public readonly showBounds: SimpleSettingsProperty<boolean>;
    public readonly showConnectors: SimpleSettingsProperty<boolean>;
    public readonly showFPS: SimpleSettingsProperty<boolean>;
    public readonly showStats: SimpleSettingsProperty<boolean>;
    
    // 🗺️ Map/Pin behavior
    public readonly hidePinSourceMeshes: SimpleSettingsProperty<boolean>;
    public readonly pinOrderingMode: SimpleSettingsProperty<string>; // 'none'|'x'|'y'|'z'|'sweep'
    public readonly pinOrderingAscending: SimpleSettingsProperty<boolean>;
    public readonly pinOrderingDirection: SimpleSettingsProperty<Vector3>;
    public readonly shapeBillboardYawOnly: SimpleSettingsProperty<boolean>;
    public readonly enableBillboardForMapAnchors: SimpleSettingsProperty<boolean>;
    
    // 🌳 Scene Defaults
    public readonly labelFontSize: InterpolatedProperty<number>;
    public readonly labelColor: SimpleSettingsProperty<Color>;
    public readonly labelBackgroundColor: SimpleSettingsProperty<Color>;
    public readonly labelBackgroundGradient: SimpleSettingsProperty<string>;
    public readonly labelFontFamily: SimpleSettingsProperty<string>;
    public readonly labelVAlign: SimpleSettingsProperty<string>;
    public readonly labelRenderMode: SimpleSettingsProperty<TextRenderMode>;
    public readonly labelPadding: SimpleSettingsProperty<[number, number]>;
    public readonly labelBorderRadius: InterpolatedProperty<number>;
	public readonly labelMaxWidth: InterpolatedProperty<number>;
	/** Kameradistanz, ab der Labels voll ausgeblendet sind (0 = kein Fade). */
	public readonly labelFadeDistance: InterpolatedProperty<number>;
	/** Mobil-Vergroesserung der Labels (Default 2 = bisheriges Verhalten). */
	public readonly mobileLabelScale: InterpolatedProperty<number>;
	/** Explizite Kameradistanz auf Mobilgeraeten (0 = aus, dann cameraDistance x 1.25). */
	public readonly mobileCameraDistance: InterpolatedProperty<number>;
	/** Textur-Einpassung nicht-rechteckiger Flaechen: cover (fuellen) | contain (ganz sichtbar). */
	public readonly textureFit: SimpleSettingsProperty<string>;
	/** Name of the ThemeRegistry preset the GLOBAL block selected (`theme:`); empty = none. */
	public readonly theme: SimpleSettingsProperty<string>;
    public readonly labelBackgroundMode: SimpleSettingsProperty<'block' | 'line'>;
    public readonly showConnector: SimpleSettingsProperty<boolean>;
    public readonly connectorColor: SimpleSettingsProperty<Color>;
    public readonly connectorThickness: InterpolatedProperty<number>;
    public readonly labelMeshPoint: InterpolatedProperty<Vector3>;
    public readonly labelScale: InterpolatedProperty<number>;
    public readonly labelBillboardMode: SimpleSettingsProperty<boolean>;
    
    // 🎯 Global Label Positioning Defaults
    public readonly labelDistance: InterpolatedProperty<number>;
    public readonly labelLatitude: InterpolatedProperty<number>;
    public readonly labelLongitude: InterpolatedProperty<number>;
    
    // 🎯 Mesh Point Indicator Settings
    public readonly showMeshPointNumbers: SimpleSettingsProperty<boolean>;
    public readonly meshPointColor: SimpleSettingsProperty<Color>;
    public readonly meshPointBackgroundColor: SimpleSettingsProperty<Color>;
    
    // 🎭 Label Animation settings
    public readonly fadeLabelOnActive: SimpleSettingsProperty<boolean>;
    public readonly labelOpacity: InterpolatedProperty<number>;
    
    // 🎯 Label Hierarchy settings
    public readonly labelParentMode: SimpleSettingsProperty<LabelParentMode>;
    
    // 🎨 Texture Rendering settings
    public readonly textureBacksideMode: SimpleSettingsProperty<TextureBacksideMode>;
    public readonly textureBacksideFlipX: SimpleSettingsProperty<boolean>;
    
    constructor(initialData?: any) {
        this.id = crypto.randomUUID();
        const cameraGroup = 'Camera';
        const mobileCameraGroup = 'Mobile Camera';
        const performanceGroup = 'Performance';
        const debugGroup = 'Debug';
        const labelGroup = 'Labels';
        const pinsGroup = 'Pins';
        const connectorGroup = 'Connector';
        const renderingGroup = 'Rendering';

        // 📷 Initialize camera settings with "Camera" group
        this.cameraRotationMode = new SimpleSettingsProperty(
            'Camera Rotation Mode', 
            null, 
            CameraRotationMode.FIXED_OFFSET, 
            cameraGroup, 
            Object.values(CameraRotationMode)
        );
        this.cameraDistance = new InterpolatedProperty('Camera Distance', null, getMobileAwareCameraDistance(), 2, cameraGroup).withBounds(0.01, 20, 0.01);
        this.cameraLatitude = new InterpolatedProperty('Camera Latitude', null, getMobileAwareCameraLatitude(), 2, cameraGroup).withBounds(-180, 180, 1);
        this.cameraLongitude = new InterpolatedProperty('Camera Longitude', null, 0, 2, cameraGroup).withBounds(-180, 180, 1);
        this.cameraTranslationSpeed = new InterpolatedProperty('Translation Speed', null, 18, 2, cameraGroup).withBounds(0.1, 20, 0.1);
        this.cameraRotationSpeed = new InterpolatedProperty('Rotation Speed', null, 18, 2, cameraGroup).withBounds(0.1, 20, 0.1);
        
        // 📱 Initialize mobile camera settings with "Mobile Camera" group
        this.enableMobileCameraOptimization = new SimpleSettingsProperty('Enable Mobile Optimization', null, true, mobileCameraGroup);
        this.mobileCameraDistanceMultiplier = new InterpolatedProperty('Distance Multiplier', null, 0.58, 0.5, mobileCameraGroup).withBounds(0.1, 2.0, 0.01);
        this.mobileCameraLatitudeOffset = new InterpolatedProperty('Latitude Offset', null, 4, 0.5, mobileCameraGroup).withBounds(-45, 45, 1);
        this.mobileCameraSensitivityMultiplier = new InterpolatedProperty('Touch Sensitivity', null, 2.5, 0.2, mobileCameraGroup).withBounds(0.5, 5.0, 0.1);
        
        // ⚡ Initialize performance settings with "Performance" group
        this.lodDistance = new InterpolatedProperty('LOD Distance', null, 100, 1.0, 'Performance').withBounds(10, 500, 10);
        this.cullingDistance = new InterpolatedProperty('Culling Distance', null, 1000, 1.0, 'Performance').withBounds(100, 5000, 100);
        this.enableFrustumCulling = new SimpleSettingsProperty('Enable Frustum Culling', null, true, 'Performance');
        
        // 🐛 Initialize debug settings with "Debug" group
        this.showDebugInfo = new SimpleSettingsProperty('Show Debug Info', null, false, debugGroup);
        this.showBounds = new SimpleSettingsProperty('Show Bounds', null, false, 'Debug');
        this.showConnectors = new SimpleSettingsProperty('Show Connectors', null, true, 'Debug');
        this.showFPS = new SimpleSettingsProperty('Show FPS', null, false, 'Debug');
        this.showStats = new SimpleSettingsProperty('Show Stats', null, false, 'Debug');
        
        // 🗺️ Map/Pin behavior (global app setting)
        this.hidePinSourceMeshes = new SimpleSettingsProperty('Hide Pin Source Meshes (from GLB)', null, false, 'Scene Defaults');
        this.pinOrderingMode = new SimpleSettingsProperty('Pin Ordering Mode', null, 'x', pinsGroup, ['none', 'x', 'y', 'z', 'sweep']);
        this.pinOrderingAscending = new SimpleSettingsProperty('Pin Ordering Ascending', null, true, pinsGroup);
        this.pinOrderingDirection = new SimpleSettingsProperty('Pin Ordering Direction', null, new Vector3(1,0,0), pinsGroup);
        this.shapeBillboardYawOnly = new SimpleSettingsProperty('Shape Billboard Yaw-Only', null, false, pinsGroup);
        this.enableBillboardForMapAnchors = new SimpleSettingsProperty('Enable Billboard For Map Anchors (Yaw Only)', null, true, pinsGroup);
        
        // 🌳 Initialize Scene Defaults
        this.labelMeshPoint = new InterpolatedProperty('Default Label Mesh Point', null, Vector3.zero(), 0.5, labelGroup);
        // Keep typography authoring device-independent. LabelMesh applies the
        // mobile presentation scale once at render time, so defaults and
        // explicitly authored font sizes follow the same responsive rule.
        this.labelFontSize = new InterpolatedProperty('Default Label Font Size', null, 14, 0.2, labelGroup).withBounds(8, 48, 1);
        this.labelColor = new SimpleSettingsProperty('Default Label Color', null, new Color('#ffffff'), labelGroup);
        this.labelBackgroundColor = new SimpleSettingsProperty('Default Label Background Color', null, new Color('#000000'), labelGroup);
        this.labelBackgroundGradient = new SimpleSettingsProperty('Default Label Background Gradient', null, '', labelGroup);
        this.labelFontFamily = new SimpleSettingsProperty('Label Font Family', null, 'Arial', labelGroup, ['Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Georgia', 'Comic Sans MS']);
        this.labelVAlign = new SimpleSettingsProperty('Label Vertical Align', null, 'middle', labelGroup, ['top', 'middle', 'bottom']);
        this.labelRenderMode = new SimpleSettingsProperty('Label Render Mode', null, TextRenderMode.CANVAS, labelGroup, [TextRenderMode.HTML, TextRenderMode.CANVAS, TextRenderMode.SCREEN_HTML, TextRenderMode.SCREEN_HTML_STATIC]);
        this.labelPadding = new SimpleSettingsProperty('Default Label Padding', null, [4, 8] as [number, number], labelGroup);
		this.labelBorderRadius = new InterpolatedProperty('Label Border Radius', null, 4, 0.2, labelGroup).withBounds(0, 20, 1);
		// Max width for HTML labels (0 = auto/unset)
		this.labelMaxWidth = new InterpolatedProperty('Label Max Width (px)', null, 0, 0.2, labelGroup).withBounds(0, 2000, 1);
		this.labelFadeDistance = new InterpolatedProperty('Label Fade Distance', null, 0, 0.2, labelGroup).withBounds(0, 200, 0.5);
		this.mobileLabelScale = new InterpolatedProperty('Mobile Label Scale', null, 2, 0.2, labelGroup).withBounds(0.5, 4, 0.1);
		this.mobileCameraDistance = new InterpolatedProperty('Mobile Camera Distance', null, 0, 2, 'Scene Defaults').withBounds(0, 50, 0.1);
		this.textureFit = new SimpleSettingsProperty<string>('Texture Fit', null, 'cover', 'Scene Defaults', ['cover', 'contain']);
		this.theme = new SimpleSettingsProperty<string>('Theme', null, '', 'Scene Defaults');
        this.labelBackgroundMode = new SimpleSettingsProperty('Label Background Mode', null, 'line', labelGroup, ['block', 'line']);
        this.showConnector = new SimpleSettingsProperty('Show', null, true, connectorGroup);
        this.connectorColor = new SimpleSettingsProperty('Color', null, new Color('#ffffff'), connectorGroup);
        this.connectorThickness = new InterpolatedProperty('Thickness', null, 1, 0.2, connectorGroup).withBounds(0, 10, 0.1);
        this.labelScale = new InterpolatedProperty('Label Scale', null, 1, 0.5, labelGroup).withBounds(0.1, 10, 0.1);
        this.labelBillboardMode = new SimpleSettingsProperty('Label Billboard Mode', null, false, labelGroup);
        
        // 🎯 Initialize Global Label Positioning Defaults
        this.labelDistance = new InterpolatedProperty('Default Label Distance', null, 1.0, 0.5, labelGroup).withBounds(0, 10, 0.1);
        this.labelLatitude = new InterpolatedProperty('Default Label Latitude', null, 0, 0.5, labelGroup).withBounds(-90, 90, 1);
        this.labelLongitude = new InterpolatedProperty('Default Label Longitude', null, 0, 0.5, labelGroup).withBounds(-180, 180, 1);
        
        // 🎯 Initialize Mesh Point Indicator Defaults
        this.showMeshPointNumbers = new SimpleSettingsProperty('Show Mesh Point Numbers', null, false, labelGroup);
        this.meshPointColor = new SimpleSettingsProperty('Mesh Point Color', null, new Color('#ff6b6b'), labelGroup);
        this.meshPointBackgroundColor = new SimpleSettingsProperty('Mesh Point Background Color', null, new Color('#ffffff'), labelGroup);
        
        // 🎭 Initialize Label Animation settings
        this.fadeLabelOnActive = new SimpleSettingsProperty('Fade Labels on Active', null, false, 'Scene Defaults');
        this.labelOpacity = new InterpolatedProperty('Label Opacity', null, 1.0, 1, 'Scene Defaults').withBounds(0, 1, 0.01);
        
        // 🎯 Initialize Label Hierarchy settings
        this.labelParentMode = new SimpleSettingsProperty('Label Parent Mode', 
            null, 
            LabelParentMode.POI, 
            labelGroup, Object.values(LabelParentMode));
        
        // 🎨 Initialize Texture Rendering settings
        this.textureBacksideMode = new SimpleSettingsProperty('Texture Backside Rendering', 
            null, 
            TextureBacksideMode.AUTO_PNG, 
            renderingGroup, Object.values(TextureBacksideMode));
        
        this.textureBacksideFlipX = new SimpleSettingsProperty('Flip Backside Texture X', 
            null, 
            true, 
            renderingGroup);
        
        // Apply initial data if provided (backward compatibility)
        if (initialData) {
            this.loadFromObject(initialData);
        }
    }
    
    /**
     * Get all properties grouped by their group names
     * Perfect for UI generation
     */
    getGroupedProperties(): { [groupName: string]: { name: string; property: any }[] } {
        const groups: { [groupName: string]: { name: string; property: any }[] } = {};
        
        // Get all property names (updated list without unused properties)
        const propertyNames = [
            // Camera
            'cameraRotationMode', 'cameraDistance', 'cameraLatitude', 'cameraLongitude', 'cameraTranslationSpeed', 'cameraRotationSpeed',
            
            // Performance
            'lodDistance', 'cullingDistance', 'enableFrustumCulling',
            
            // Debug
            'showDebugInfo', 'showBounds', 'showConnectors', 'showFPS', 'showStats', 'hidePinSourceMeshes',

            // Scene Defaults
            'labelFontSize', 'labelColor', 'labelBackgroundColor', 'labelBackgroundGradient',
            'labelPadding', 'labelBorderRadius', 'labelBackgroundMode', 'showConnector', 'connectorColor', 'connectorThickness',
			'labelMeshPoint', 'labelScale', 'labelBillboardMode', 'labelDistance', 'labelLatitude', 'labelLongitude',
            'labelFontFamily', 'labelVAlign', 'labelRenderMode', 'showMeshPointNumbers', 'meshPointColor', 'meshPointBackgroundColor',
            'fadeLabelOnActive', 'labelOpacity', 'labelParentMode', 'labelMaxWidth', 'labelFadeDistance', 'mobileLabelScale', 'mobileCameraDistance', 'textureFit', 'theme',
            // Pins
            'pinOrderingMode', 'pinOrderingAscending', 'pinOrderingDirection', 'shapeBillboardYawOnly', 'enableBillboardForMapAnchors',
            
            // Rendering
            'textureBacksideMode', 'textureBacksideFlipX'
        ];
        
        propertyNames.forEach(propertyName => {
            const property = this[propertyName as keyof this] as any;
            if (property && property.groupName) {
                const groupName = property.groupName;
                if (!groups[groupName]) {
                    groups[groupName] = [];
                }
                groups[groupName].push({ name: propertyName, property });
            }
        });
        
        return groups;
    }
    
    /**
     * Get all properties as flat list with metadata
     */
    getAllProperties(): { name: string; property: any; group: string; type: string }[] {
        const grouped = this.getGroupedProperties();
        const result: { name: string; property: any; group: string; type: string }[] = [];
        
        Object.entries(grouped).forEach(([groupName, properties]) => {
            properties.forEach(({ name, property }) => {
                result.push({
                    name,
                    property,
                    group: groupName,
                    type: property.typeInfo.type
                });
            });
        });
        
        return result;
    }
    

    
    /**
     * Load settings from object
     */
    loadFromObject(data: any): void {
        // Camera settings
        if (data.cameraRotationMode !== undefined) this.cameraRotationMode.value = data.cameraRotationMode;
        if (data.cameraDistance !== undefined) this.cameraDistance.value = data.cameraDistance;
        if (data.cameraLatitude !== undefined) this.cameraLatitude.value = data.cameraLatitude;
        if (data.cameraLongitude !== undefined) this.cameraLongitude.value = data.cameraLongitude;
        if (data.cameraTranslationSpeed !== undefined) this.cameraTranslationSpeed.value = data.cameraTranslationSpeed;
        if (data.cameraRotationSpeed !== undefined) this.cameraRotationSpeed.value = data.cameraRotationSpeed;
        
        // Performance settings
        if (data.lodDistance !== undefined) this.lodDistance.value = data.lodDistance;
        if (data.cullingDistance !== undefined) this.cullingDistance.value = data.cullingDistance;
        if (data.enableFrustumCulling !== undefined) this.enableFrustumCulling.value = data.enableFrustumCulling;
        
        // Debug settings
        if (data.showDebugInfo !== undefined) this.showDebugInfo.value = data.showDebugInfo;
        if (data.showBoundingBoxes !== undefined) this.showBounds.value = data.showBoundingBoxes;
        if (data.showConnectors !== undefined) this.showConnectors.value = data.showConnectors;
        if (data.showFPS !== undefined) this.showFPS.value = data.showFPS;
        if (data.showStats !== undefined) this.showStats.value = data.showStats;
        if (data.hidePinSourceMeshes !== undefined) this.hidePinSourceMeshes.value = data.hidePinSourceMeshes;
        if (data.pinOrderingMode !== undefined) this.pinOrderingMode.value = data.pinOrderingMode;
        if (data.pinOrderingAscending !== undefined) this.pinOrderingAscending.value = data.pinOrderingAscending;
        if (data.pinOrderingDirection !== undefined) this.pinOrderingDirection.value = new Vector3(data.pinOrderingDirection.x ?? data.pinOrderingDirection[0] ?? 1, data.pinOrderingDirection.y ?? data.pinOrderingDirection[1] ?? 0, data.pinOrderingDirection.z ?? data.pinOrderingDirection[2] ?? 0);
        if (data.shapeBillboardYawOnly !== undefined) this.shapeBillboardYawOnly.value = data.shapeBillboardYawOnly;
        if (data.enableBillboardForMapAnchors !== undefined) this.enableBillboardForMapAnchors.value = data.enableBillboardForMapAnchors;
        
        // Scene Defaults
        if (data.labelFontSize !== undefined) this.labelFontSize.value = data.labelFontSize;
        if (data.labelColor !== undefined) this.labelColor.value = data.labelColor;
        if (data.labelBackgroundColor !== undefined) this.labelBackgroundColor.value = data.labelBackgroundColor;
        if (data.labelPadding !== undefined) this.labelPadding.value = data.labelPadding;
        if (data.labelBorderRadius !== undefined) this.labelBorderRadius.value = data.labelBorderRadius;
        if (data.labelBackgroundMode !== undefined) this.labelBackgroundMode.value = data.labelBackgroundMode;
        if (data.showConnector !== undefined) this.showConnector.value = data.showConnector;
        if (data.connectorColor !== undefined) this.connectorColor.value = data.connectorColor;
        if (data.connectorThickness !== undefined) this.connectorThickness.value = data.connectorThickness;
        if (data.labelMeshPoint !== undefined) this.labelMeshPoint.value = data.labelMeshPoint;
        if (data.labelScale !== undefined) this.labelScale.value = data.labelScale;
        if (data.labelBillboardMode !== undefined) this.labelBillboardMode.value = data.labelBillboardMode;
        if (data.labelDistance !== undefined) this.labelDistance.value = data.labelDistance;
        if (data.labelLatitude !== undefined) this.labelLatitude.value = data.labelLatitude;
        if (data.labelLongitude !== undefined) this.labelLongitude.value = data.labelLongitude;
        if (data.labelFontFamily !== undefined) this.labelFontFamily.value = data.labelFontFamily;
        if (data.labelVAlign !== undefined) this.labelVAlign.value = data.labelVAlign;
        if (data.labelRenderMode !== undefined) this.labelRenderMode.value = data.labelRenderMode;
        if (data.showMeshPointNumbers !== undefined) this.showMeshPointNumbers.value = data.showMeshPointNumbers;
        if (data.meshPointColor !== undefined) this.meshPointColor.value = data.meshPointColor;
        if (data.meshPointBackgroundColor !== undefined) this.meshPointBackgroundColor.value = data.meshPointBackgroundColor;

        // Label Animation
        if (data.fadeLabelOnActive !== undefined) this.fadeLabelOnActive.value = data.fadeLabelOnActive;
        if (data.labelOpacity !== undefined) this.labelOpacity.value = data.labelOpacity;
        
        // Texture Rendering
        if (data.textureBacksideMode !== undefined) this.textureBacksideMode.value = data.textureBacksideMode;
        if (data.textureBacksideFlipX !== undefined) this.textureBacksideFlipX.value = data.textureBacksideFlipX;
    }
    
    /**
     * Export settings to object
     */
    toObject(): any {
        const obj: any = {
            // ... non-label properties ...
        };

        // Dynamically add all properties to the object
        this.getAllProperties().forEach(({ name, property }) => {
            const key = name.charAt(0).toLowerCase() + name.slice(1);
            if (property.value instanceof Color) {
                obj[key] = property.value?.toString();
            } else if (property.value instanceof Vector3) {
                obj[key] = property.value?.toArray();
            } else {
                obj[key] = property.value;
            }
        });
        
        return obj;
    }
    
    /**
     * Reset to default values
     */
    resetToDefaults(): void {
        this.loadFromObject({
            // Camera defaults
            cameraRotationMode: CameraRotationMode.FIXED_OFFSET,
            cameraDistance: 15,
            cameraLatitude: 20,
            cameraLongitude: 0,
            cameraTranslationSpeed: 4,
            cameraRotationSpeed: 3,
            
            // Performance defaults
            lodDistance: 100,
            cullingDistance: 1000,
            enableFrustumCulling: true,
            
            // Debug defaults
            showDebugInfo: false,
            showBoundingBoxes: false,
            showConnectors: true,
            showFPS: false,
            showStats: false,
            hidePinSourceMeshes: false,
            
            // Scene Defaults
            labelFontSize: 14,
            labelColor: '#ffffff',
            labelBackgroundColor: '#000000b3',
            labelPadding: 5,
            labelBorderRadius: 4,
            labelBackgroundMode: 'line',
            showConnector: true,
            connectorColor: '#ffffff',
            connectorThickness: 0.1,
            labelMeshPoint: null,
            labelScale: 1.0,
            labelBillboardMode: false,
            labelDistance: 1.0,
            labelLatitude: 0,
            labelLongitude: 0,
            labelFontFamily: 'Arial',
            labelVAlign: 'middle',
            labelRenderMode: 'canvas',
            showMeshPointNumbers: false,
            meshPointColor: null,
            meshPointBackgroundColor: null,

            // Label Animation
            fadeLabelOnActive: false,
            labelOpacity: 1.0,
            
            // Texture Rendering
            textureBacksideMode: TextureBacksideMode.AUTO_PNG,
            textureBacksideFlipX: true
        });
    }
    
    /**
     * Clone settings
     */
    clone(): AppSettingsData {
        const cloned = new AppSettingsData();
        cloned.loadFromObject(this.toObject());
        return cloned;
    }
    
    // 🎯 NO MORE CONTROLLER LOGIC IN DATA CLASSES!
    // Components should listen directly: appSettings.cameraDistance.on(handler)
    // Instead of: appSettings.onPropertyChanged(handler)
} 
